// @flow

import React from 'react';
import { FormattedMessage } from 'react-intl';

// Components
import EmptyTagIcon from 'dibs-vg/dist/react/tag-empty';
import { Tooltip } from 'dibs-elements/exports/Tooltip';

// Helpers
import classnames from 'classnames';
import get from 'lodash.get';

// Styles
import styles from './styles/TagProjectCTA.scss';

type ProjectsType = Array<{
    name: string,
    serviceId: string,
}>;

type TagProjectCTAProps = {
    className?: string,
    disabled?: boolean,
    canAddProject?: boolean,
    displayInSingleRow?: boolean,
    onClick: (e: SyntheticInputEvent<*>) => void,
    projects: ProjectsType,
};

type TagProjectCTAState = {
    showTooltip: boolean,
};

type TooltipContainerProps = {
    projects: ProjectsType,
};

export const TooltipContainer = ({ projects }: TooltipContainerProps) => {
    return (
        <Tooltip isVisible direction="bottom">
            {projects.slice(1).map(({ name }) => (
                <div className={styles.ellipsis} title={name} key={name}>
                    {name}
                </div>
            ))}
        </Tooltip>
    );
};

class TagProjectCTA extends React.Component<TagProjectCTAProps, TagProjectCTAState> {
    constructor(props: TagProjectCTAProps) {
        super(props);

        this.state = {
            showTooltip: false,
        };

        this.renderOtherProjects = this.renderOtherProjects.bind(this);
        this.showProjectsTooltip = this.showProjectsTooltip.bind(this);
    }

    renderOtherProjects: (projects: ProjectsType) => React$Element<*>;
    showProjectsTooltip: (state: TagProjectCTAState) => void;

    renderOtherProjects(projects: ProjectsType) {
        if (projects.length > 1) {
            const { displayInSingleRow, onClick } = this.props;
            const containerClasses = classnames(styles.linksContainer, {
                [styles.singleRow]: displayInSingleRow,
            });
            return (
                <div className={containerClasses}>
                    <div
                        onMouseLeave={() => this.showProjectsTooltip({ showTooltip: false })}
                        onMouseOver={() => this.showProjectsTooltip({ showTooltip: true })}
                        className={styles.otherProjectsLink}
                        data-tn="other-projects-link"
                    >
                        {this.state.showTooltip ? <TooltipContainer projects={projects} /> : null}
                        {'+ '}
                        <FormattedMessage
                            id="dc.ProjectForms.TagProjectCTA.otherProjects"
                            defaultMessage={
                                '{count, number} {count, plural, one {other} other {others}}'
                            }
                            values={{ count: projects.length - 1 }}
                        />
                    </div>
                    {!displayInSingleRow && (
                        <div
                            onClick={onClick}
                            className={styles.addMoreLink}
                            data-tn="add-more-cta"
                        >
                            {'+ '}
                            <FormattedMessage
                                id="dc.ProjectForms.TagProjectCTA.addMore"
                                defaultMessage="Add more"
                            />
                        </div>
                    )}
                </div>
            );
        }
        return null;
    }

    showProjectsTooltip({ showTooltip = false }: TagProjectCTAState) {
        this.setState({ showTooltip });
    }

    render() {
        const {
            className,
            disabled,
            canAddProject,
            displayInSingleRow,
            onClick,
            projects,
        } = this.props;
        const projectClasses = classnames(styles.project, className, {
            [styles.disabled]: disabled,
        });
        const addProjectClasses = classnames(projectClasses, styles.add);
        const projectNameClasses = classnames(styles.projectName, {
            [styles.cappedWidth]: displayInSingleRow && (projects && projects.length > 1),
        });

        const projectName = Array.isArray(projects) ? get(projects, '[0].name') : '';

        if (projectName) {
            return (
                <div
                    className={projectClasses}
                    data-tn="project-info"
                    onClick={event => {
                        event.preventDefault();
                    }}
                >
                    <div onClick={onClick} className={styles.projectNameContainer}>
                        <EmptyTagIcon className={styles.icon} />
                        <div
                            className={projectNameClasses}
                            title={projectName}
                            data-tn="project-name"
                        >
                            {projectName}
                        </div>
                        {displayInSingleRow && this.renderOtherProjects(projects)}
                    </div>
                    {!displayInSingleRow && this.renderOtherProjects(projects)}
                </div>
            );
        } else if (canAddProject) {
            return (
                <div className={addProjectClasses} data-tn="project-link" onClick={onClick}>
                    <div className={styles.projectNameContainer}>
                        <EmptyTagIcon className={styles.icon} />
                        <div className={styles.projectName}>
                            <FormattedMessage
                                id="dc.ProjectForms.TagProjectCTA.addProject"
                                defaultMessage="Add project tag"
                            />
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }
}

export default TagProjectCTA;
